
import { defineComponent, PropType } from 'vue'

import { ReportMeta, ReportPageT, ReportsTabLink } from '~/types/reportTypes'
import { ReportLevel } from '~/types/types'

/**
 * Sidebar component for public report page
 */
export default defineComponent({
  name: 'ReportsSidebar',
  props: {
    level: {
      type: String as PropType<ReportLevel>,
      required: true
    },
    showPublicReports: {
      type: Boolean,
      required: true
    }
  },
  computed: {
    ReportLevel() {
      return ReportLevel
    },
    sidebarClass() {
      const classMap = {
        [ReportLevel.Repository]: 'vertical-sidebar-repository-level',
        [ReportLevel.RepositoryGroup]: 'vertical-sidebar-repository-group-level',
        [ReportLevel.Owner]: 'vertical-sidebar-owner-level',
        [ReportLevel.Enterprise]: 'vertical-sidebar-enterprise-level'
      }
      return classMap[this.level] || 'vertical-sidebar-owner-level'
    },
    complianceItems(): ReportsTabLink[] {
      return [
        {
          key: ReportPageT.OWASP_TOP_10,
          label: ReportMeta[ReportPageT.OWASP_TOP_10].title
        },
        {
          key: ReportPageT.SANS_TOP_25,
          label: ReportMeta[ReportPageT.SANS_TOP_25].title
        },
        {
          key: ReportPageT.MISRA_C,
          label: ReportMeta[ReportPageT.MISRA_C].title
        }
      ]
    },
    insightsItems(): ReportsTabLink[] {
      return [
        {
          key: ReportPageT.CODE_HEALTH_TREND,
          label: ReportMeta[ReportPageT.CODE_HEALTH_TREND].title
        },
        {
          key: ReportPageT.CODE_COVERAGE,
          label: ReportMeta[ReportPageT.CODE_COVERAGE].title
        },
        {
          key: ReportPageT.SCA_TARGETS,
          label: ReportMeta[ReportPageT.SCA_TARGETS].title
        },
        {
          key: ReportPageT.ISSUES_PREVENTED,
          label: ReportMeta[ReportPageT.ISSUES_PREVENTED].title
        },
        {
          key: ReportPageT.ISSUES_AUTOFIXED,
          label: ReportMeta[ReportPageT.ISSUES_AUTOFIXED].title
        },
        {
          key: ReportPageT.DISTRIBUTION,
          label: ReportMeta[ReportPageT.DISTRIBUTION].title
        },
        {
          key: ReportPageT.ACTIVE_THIRD_PARTY_VULNERABILITIES,
          label: ReportMeta[ReportPageT.ACTIVE_THIRD_PARTY_VULNERABILITIES].title
        }
      ]
    },
    publicReportsItem(): ReportsTabLink {
      return {
        key: ReportPageT.PUBLIC_REPORTS,
        label: ReportMeta[ReportPageT.PUBLIC_REPORTS].title
      }
    },
    rootPath(): string {
      if (this.level === ReportLevel.Repository) {
        return 'provider-owner-repo-reports'
      }
      if (this.level === ReportLevel.RepositoryGroup) {
        return 'orgs-provider-owner-groups-groupSlug-reports'
      }
      if (this.level === ReportLevel.Owner) {
        return 'orgs-provider-owner-reports'
      }
      return ''
    },
    navItemsForMobile() {
      const visibleNavItems = [
        ...this.complianceItems,
        ...this.insightsItems,
        { ...this.publicReportsItem, separator: true }
      ].filter(({ key }) => this.showReport(key))

      return visibleNavItems.map((item) => {
        return {
          label: item.label,
          routePath: this.getRoute(item.key),
          separator: 'separator' in item ? item.separator : false
        }
      })
    }
  },
  methods: {
    /**
     * Method to check whether to show the report or not
     *
     * @param {ReportPageT} reportKey
     */
    showReport(reportKey: ReportPageT): boolean {
      return ReportMeta[reportKey].level.includes(this.level)
    },
    /*
      at owner level, the route would be `orgs-provider-owner-reports-<reportKey>`
      at repo level, the route would be `provider-owner-repo-reports-<reportKey>`
    */
    getRoute(candidate: string) {
      const atRepoLevel = this.level === ReportLevel.Repository
      const atOwnerLevel = this.level === ReportLevel.Owner
      return {
        name: atRepoLevel
          ? `provider-owner-repo-reports-${candidate}`
          : atOwnerLevel
            ? `orgs-provider-owner-reports-${candidate}`
            : `orgs-provider-owner-groups-groupSlug-reports-${candidate}`,
        params: this.$route.params
      }
    }
  }
})
